import React, { useMemo } from 'react';
import styled from 'styled-components';
import { Row, Col, Typography, Table, Tag, Statistic, Card, Alert, Divider } from 'antd';
import {
  DatabaseOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  CloudOutlined,
  WarningOutlined,
  LaptopOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import { StatCard, DataCard } from '../components/common';
import { SaasProviderCard, SaasAssetsTable } from '../components/datto';
import { useTenant } from '../context/TenantContext';
import { isM365Managed } from '../utils/customerKey';
import {
  useDattoIdFromCipp,
  useDattoDevices,
  useDattoAlerts,
  useDattoProtectedAssets,
  useDattoSaasDomains,
  useDattoEndpointAgents,
} from '../hooks';
import {
  identifySaasProvider,
  categorizeSaasAssets,
  calculateSaasMetrics,
  parseMCPResponse,
  saasDomainToAsset,
} from '../utils/dattoHelpers';

const { Title, Text } = Typography;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const MappingAlert = styled(Alert)`
  margin-bottom: 24px;
`;

const DattoPage = () => {
  const { selectedTenant } = useTenant();
  const cippTenantId = selectedTenant?.id;

  // Resolve CIPP tenant ID to Datto identifiers using customer mapping
  const {
    datto,              // New: normalized object with both IDs
    dattoClient,        // Legacy: organizationId
    isResolved: hasDattoMapping,
    isLoading: isMappingLoading,
    customer,
  } = useDattoIdFromCipp(cippTenantId, {
    enabled: !!cippTenantId,
  });

  // Fetch Datto devices
  const { data: devicesData, isLoading: devicesLoading } = useDattoDevices(dattoClient, {
    enabled: !!dattoClient,
  });

  // Fetch Datto alerts
  const { data: alertsData, isLoading: alertsLoading } = useDattoAlerts(dattoClient, {
    enabled: !!dattoClient,
  });

  // Fetch Datto protected assets (BCDR agents only - SaaS not included here)
  const { data: assetsData, isLoading: assetsLoading, error: assetsError } = useDattoProtectedAssets(
    dattoClient,
    'all',
    { enabled: !!dattoClient }
  );

  // Fetch Datto SaaS Protection domains (M365, Google Workspace)
  const { data: saasData, isLoading: saasLoading, error: saasError } = useDattoSaasDomains(
    {
      organizationId: datto?.organizationId,
      saasCustomerId: datto?.saasCustomerId,
    },
    { enabled: hasDattoMapping && (datto?.hasBCDR || datto?.hasSaaS) }
  );

  // Fetch Datto endpoint backup agents (PC agents)
  const { data: agentsData, isLoading: agentsLoading, error: agentsError } = useDattoEndpointAgents(
    dattoClient,
    { enabled: !!dattoClient }
  );

  // Calculate SaaS metrics (combining BCDR assets + SaaS domains)
  const saasMetrics = useMemo(() => {
    console.log('[DattoPage] Raw saasData:', saasData);

    // Parse BCDR assets (legacy - may include some SaaS if they exist in BCDR API)
    const parsedAssets = parseMCPResponse(assetsData);
    const bcdrAssets = parsedAssets?.assets || parsedAssets?.data?.assets || [];

    // Parse SaaS domains from new API
    const parsedSaas = parseMCPResponse(saasData);
    console.log('[DattoPage] Parsed SaaS:', parsedSaas);

    const saasDomains = parsedSaas?.domains || parsedSaas?.data?.domains || [];
    console.log('[DattoPage] SaaS domains count:', saasDomains.length);
    console.log('[DattoPage] SaaS domains:', saasDomains);

    // Convert SaaS domains to the asset shape the card/table render (status from
    // service counts, no lastBackup - the SaaS record has no timestamp).
    const saasAssets = saasDomains.map(saasDomainToAsset);

    console.log('[DattoPage] Transformed SaaS assets:', saasAssets);

    // Combine both sources
    const allAssets = [...bcdrAssets, ...saasAssets];

    const metrics = calculateSaasMetrics(allAssets);
    console.log('[DattoPage] Final SaaS metrics:', metrics);

    return metrics;
  }, [assetsData, saasData]);

  const saasAssetsByProvider = useMemo(() => {
    // Parse BCDR assets
    const parsedAssets = parseMCPResponse(assetsData);
    const bcdrAssets = parsedAssets?.assets || parsedAssets?.data?.assets || [];

    // Parse SaaS domains
    const parsedSaas = parseMCPResponse(saasData);
    const saasDomains = parsedSaas?.domains || parsedSaas?.data?.domains || [];

    // Convert SaaS domains to the asset shape the card/table render (status from
    // service counts, no lastBackup - the SaaS record has no timestamp).
    const saasAssets = saasDomains.map(saasDomainToAsset);

    // Combine both sources
    const allAssets = [...bcdrAssets, ...saasAssets];

    return categorizeSaasAssets(allAssets);
  }, [assetsData, saasData]);

  // Parse and calculate endpoint agent metrics
  const { agents, totalAgents, healthyAgents, warningAgents, offlineAgents } = useMemo(() => {
    const parsedAgents = parseMCPResponse(agentsData);
    const agentsList = Array.isArray(parsedAgents?.agents)
      ? parsedAgents.agents
      : Array.isArray(parsedAgents?.data?.agents)
        ? parsedAgents.data.agents
        : [];

    const healthy = agentsList.filter(a => a.status === 'healthy').length;
    const warning = agentsList.filter(a => a.status === 'warning').length;
    const offline = agentsList.filter(a => a.status === 'offline').length;

    return {
      agents: agentsList,
      totalAgents: agentsList.length,
      healthyAgents: healthy,
      warningAgents: warning,
      offlineAgents: offline,
    };
  }, [agentsData]);

  // Parse and calculate BCDR protected assets metrics
  const { bcdrAssets, totalBcdrAssets, protectedCount, unprotectedCount, warningCount } = useMemo(() => {
    const parsedAssets = parseMCPResponse(assetsData);
    const allAssets = Array.isArray(parsedAssets?.assets)
      ? parsedAssets.assets
      : Array.isArray(parsedAssets?.data?.assets)
        ? parsedAssets.data.assets
        : [];

    // Filter to BCDR assets only (exclude SaaS)
    // Include: VM, Physical, FileShare, Cloud, and agent (agent-based server backups)
    const bcdrOnly = allAssets.filter(a =>
      a.type !== 'SaaS' && ['VM', 'Physical', 'FileShare', 'Cloud', 'agent'].includes(a.type)
    );

    const protectedCount = bcdrOnly.filter(a => a.status === 'protected').length;
    const unprotectedCount = bcdrOnly.filter(a => a.status === 'unprotected').length;
    const warningCount = bcdrOnly.filter(a => a.status === 'warning').length;

    return {
      bcdrAssets: bcdrOnly,
      totalBcdrAssets: bcdrOnly.length,
      protectedCount,
      unprotectedCount,
      warningCount,
    };
  }, [assetsData]);

  // Parse and calculate metrics
  const { devices, deviceCount, onlineDevices, offlineDevices, criticalAlerts, warningAlerts } = useMemo(() => {
    // Parse devices from Datto-MCP response
    let parsedDevices = null;
    if (devicesData) {
      if (typeof devicesData === 'string') {
        try {
          parsedDevices = JSON.parse(devicesData);
        } catch (e) {
          console.error('[DattoPage] Failed to parse devices data:', e);
        }
      } else {
        parsedDevices = devicesData;
      }
    }

    // Parse alerts from Datto-MCP response
    let parsedAlerts = null;
    if (alertsData) {
      if (typeof alertsData === 'string') {
        try {
          parsedAlerts = JSON.parse(alertsData);
        } catch (e) {
          console.error('[DattoPage] Failed to parse alerts data:', e);
        }
      } else {
        parsedAlerts = alertsData;
      }
    }

    const devicesList = parsedDevices?.devices || parsedDevices?.data?.devices || [];
    const alertsList = parsedAlerts?.alerts || parsedAlerts?.data?.alerts || [];

    const online = devicesList.filter(d => d.status === 'online' || d.status === 'Online').length;
    const critical = alertsList.filter(a => a.severity === 'critical' || a.severity === 'Critical').length;
    const warning = alertsList.filter(a => a.severity === 'warning' || a.severity === 'Warning').length;

    return {
      devices: devicesList,
      deviceCount: devicesList.length,
      onlineDevices: online,
      offlineDevices: devicesList.length - online,
      criticalAlerts: critical,
      warningAlerts: warning,
    };
  }, [devicesData, alertsData]);

  const isLoading = isMappingLoading || devicesLoading || alertsLoading || assetsLoading || saasLoading || agentsLoading;
  const showMappingWarning = cippTenantId && !hasDattoMapping && !isMappingLoading;

  // Device table columns
  const deviceColumns = [
    {
      title: 'Device Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => name || record.deviceName || 'Unknown Device',
      sorter: (a, b) => (a.name || a.deviceName || '').localeCompare(b.name || b.deviceName || ''),
    },
    {
      title: 'Serial Number',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      render: (serial, record) => (
        <Text code style={{ fontSize: 12 }}>{serial || record.serial || 'N/A'}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const isOnline = status === 'online' || status === 'Online';
        return (
          <Tag
            color={isOnline ? 'green' : 'red'}
            icon={isOnline ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
          >
            {status || 'Unknown'}
          </Tag>
        );
      },
      filters: [
        { text: 'Online', value: 'online' },
        { text: 'Online', value: 'Online' },
        { text: 'Offline', value: 'offline' },
        { text: 'Offline', value: 'Offline' },
      ],
      onFilter: (value, record) => {
        const status = (record.status || '').toLowerCase();
        return status === value.toLowerCase();
      },
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <DatabaseOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Datto BCDR
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          {selectedTenant
            ? `${selectedTenant.displayName} - Datto Backup & Disaster Recovery${customer ? ` (${customer.displayName})` : ''}`
            : 'All Tenants - Datto BCDR Status'}
        </Text>
      </PageHeader>

      {showMappingWarning && (
        <MappingAlert
          message={
            <span>
              <LinkOutlined style={{ marginRight: 8 }} />
              {isM365Managed(selectedTenant)
                ? 'No Datto mapping found for this tenant. Configure the mapping in Odin-MCP customer configuration or click "Sync Platforms" on the Dashboard.'
                : 'No Datto mapping found for this customer. Add it in Admin → Customer Mappings.'}
            </span>
          }
          type="warning"
          showIcon={false}
        />
      )}

      {/* SaaS-only alert */}
      {hasDattoMapping && datto?.hasSaaS && !datto?.hasBCDR && (
        <Alert
          message="SaaS Protection Only"
          description="This customer has Datto SaaS Protection (M365/Google Workspace) but no BCDR devices."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* BCDR-only alert */}
      {hasDattoMapping && datto?.hasBCDR && !datto?.hasSaaS && (
        <Alert
          message="BCDR Only"
          description="This customer has Datto BCDR devices but no SaaS Protection subscriptions."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Stats Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={DatabaseOutlined}
            value={deviceCount}
            label="BCDR Devices"
            loading={isLoading}
            tooltip="Total Datto BCDR devices for this customer"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={CheckCircleOutlined}
            value={onlineDevices}
            label="Online Devices"
            color="var(--odin-success)"
            loading={isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={DatabaseOutlined}
            value={protectedCount}
            label="Protected Systems"
            color="var(--odin-status-active-text)"
            loading={isLoading}
            tooltip="BCDR protected systems (VMs, Physical servers, FileShares)"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={LaptopOutlined}
            value={totalAgents}
            label="PC Backup Agents"
            color="var(--odin-text-secondary)"
            loading={isLoading}
            tooltip="Total Datto Endpoint Backup agents protecting PCs"
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <StatCard
            icon={ExclamationCircleOutlined}
            value={criticalAlerts}
            label="Critical Alerts"
            color="var(--odin-error)"
            loading={isLoading}
          />
        </Col>
      </Row>

      {/* BCDR Devices Table */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <DataCard
            title="BCDR Devices"
            icon={DatabaseOutlined}
            loading={isLoading}
            empty={!devices?.length && !showMappingWarning}
            emptyText={selectedTenant ? 'No devices found' : 'Select a tenant to view devices'}
          >
            <StyledTable
              dataSource={devices || []}
              columns={deviceColumns}
              rowKey={(record) => record.serialNumber || record.serial || record.id || Math.random().toString()}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>

      {/* Endpoint Backup Agents Table */}
      {hasDattoMapping && totalAgents > 0 && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24}>
            <DataCard
              title="PC Backup Agents"
              icon={LaptopOutlined}
              loading={agentsLoading}
              error={agentsError}
              empty={!agents?.length}
              emptyText="No PC backup agents found"
            >
              <StyledTable
                dataSource={agents || []}
                columns={[
                  {
                    title: 'Hostname',
                    dataIndex: 'hostname',
                    key: 'hostname',
                    render: (hostname) => hostname || 'Unknown PC',
                    sorter: (a, b) => (a.hostname || '').localeCompare(b.hostname || ''),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status) => {
                      const statusConfig = {
                        healthy: { color: 'green', icon: <CheckCircleOutlined /> },
                        warning: { color: 'orange', icon: <WarningOutlined /> },
                        offline: { color: 'red', icon: <CloseCircleOutlined /> },
                      };
                      const config = statusConfig[status] || statusConfig.offline;
                      return (
                        <Tag color={config.color} icon={config.icon}>
                          {status || 'Unknown'}
                        </Tag>
                      );
                    },
                    filters: [
                      { text: 'Healthy', value: 'healthy' },
                      { text: 'Warning', value: 'warning' },
                      { text: 'Offline', value: 'offline' },
                    ],
                    onFilter: (value, record) => record.status === value,
                  },
                  {
                    title: 'Last Snapshot',
                    dataIndex: 'lastSnapshot',
                    key: 'lastSnapshot',
                    render: (timestamp) => {
                      if (!timestamp) return <Text type="secondary">Never</Text>;
                      const date = new Date(timestamp * 1000);
                      const now = Date.now();
                      const ageHours = (now - date.getTime()) / (1000 * 60 * 60);

                      let color = 'var(--odin-success)';
                      if (ageHours > 48) color = 'var(--odin-warning)';
                      if (ageHours > 72) color = 'var(--odin-error)';

                      return (
                        <Text style={{ color }}>
                          {date.toLocaleString()}
                        </Text>
                      );
                    },
                    sorter: (a, b) => (a.lastSnapshot || 0) - (b.lastSnapshot || 0),
                  },
                  {
                    title: 'Agent ID',
                    dataIndex: 'shortCode',
                    key: 'shortCode',
                    render: (code) => (
                      <Text code style={{ fontSize: 12 }}>{code || 'N/A'}</Text>
                    ),
                  },
                ]}
                rowKey={(record) => record.uuid || record.id || Math.random().toString()}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                size="small"
              />
            </DataCard>
          </Col>
        </Row>
      )}

      {/* BCDR Protected Systems Table */}
      {hasDattoMapping && totalBcdrAssets > 0 && (
        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24}>
            <DataCard
              title="Protected Systems (BCDR)"
              icon={DatabaseOutlined}
              loading={assetsLoading}
              error={assetsError}
              empty={!bcdrAssets?.length}
              emptyText="No protected systems found"
            >
              <StyledTable
                dataSource={bcdrAssets || []}
                columns={[
                  {
                    title: 'Asset Name',
                    dataIndex: 'name',
                    key: 'name',
                    render: (name) => name || 'Unknown Asset',
                    sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
                  },
                  {
                    title: 'Type',
                    dataIndex: 'type',
                    key: 'type',
                    render: (type) => (
                      <Tag color={type === 'VM' ? 'blue' : type === 'Physical' ? 'green' : 'default'}>
                        {type || 'Unknown'}
                      </Tag>
                    ),
                    filters: [
                      { text: 'VM', value: 'VM' },
                      { text: 'Physical', value: 'Physical' },
                      { text: 'FileShare', value: 'FileShare' },
                      { text: 'Cloud', value: 'Cloud' },
                    ],
                    onFilter: (value, record) => record.type === value,
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (status) => {
                      const statusConfig = {
                        protected: { color: 'green', icon: <CheckCircleOutlined /> },
                        warning: { color: 'orange', icon: <WarningOutlined /> },
                        unprotected: { color: 'red', icon: <CloseCircleOutlined /> },
                      };
                      const config = statusConfig[status] || statusConfig.unprotected;
                      return (
                        <Tag color={config.color} icon={config.icon}>
                          {status || 'Unknown'}
                        </Tag>
                      );
                    },
                    filters: [
                      { text: 'Protected', value: 'protected' },
                      { text: 'Warning', value: 'warning' },
                      { text: 'Unprotected', value: 'unprotected' },
                    ],
                    onFilter: (value, record) => record.status === value,
                  },
                  {
                    title: 'Last Backup',
                    dataIndex: 'lastBackup',
                    key: 'lastBackup',
                    render: (lastBackup) => {
                      if (!lastBackup) return <Text type="secondary">Never</Text>;
                      const date = new Date(lastBackup);
                      const now = Date.now();
                      const ageHours = (now - date.getTime()) / (1000 * 60 * 60);

                      let color = 'var(--odin-success)';
                      if (ageHours > 24) color = 'var(--odin-warning)';
                      if (ageHours > 48) color = 'var(--odin-error)';

                      return (
                        <Text style={{ color }}>
                          {date.toLocaleString()}
                        </Text>
                      );
                    },
                    sorter: (a, b) => {
                      const aTime = a.lastBackup ? new Date(a.lastBackup).getTime() : 0;
                      const bTime = b.lastBackup ? new Date(b.lastBackup).getTime() : 0;
                      return bTime - aTime;
                    },
                  },
                  {
                    title: 'Size',
                    dataIndex: 'size',
                    key: 'size',
                    render: (size) => {
                      if (!size) return <Text type="secondary">N/A</Text>;
                      const gb = size / 1024 / 1024 / 1024;
                      return <Text>{gb.toFixed(2)} GB</Text>;
                    },
                    sorter: (a, b) => (a.size || 0) - (b.size || 0),
                  },
                ]}
                rowKey={(record) => record.id || record.name || Math.random().toString()}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                size="small"
              />
            </DataCard>
          </Col>
        </Row>
      )}

      {/* SaaS Protection Section - Always visible if mapped */}
      {hasDattoMapping && (
        <>
          <Divider
            style={{
              color: 'var(--odin-text-tertiary)',
              marginTop: 32,
              fontSize: '16px',
              fontWeight: 500,
            }}
          >
            <CloudOutlined /> SaaS Protection
          </Divider>

          {saasMetrics.total > 0 ? (
            <>
              {/* Provider Summary Cards */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                {Object.entries(saasAssetsByProvider).map(([provider, assets]) => {
                  const { icon, color } = identifySaasProvider(assets[0]);
                  return (
                    <Col key={provider} xs={24} sm={12} md={8} lg={6}>
                      <SaasProviderCard
                        provider={provider}
                        assets={assets}
                        icon={icon}
                        color={color}
                      />
                    </Col>
                  );
                })}
              </Row>

              {/* SaaS Assets Table */}
              <Row gutter={[24, 24]}>
                <Col xs={24}>
                  <DataCard
                    title="SaaS Assets"
                    icon={CloudOutlined}
                    loading={assetsLoading || saasLoading}
                    error={assetsError || saasError}
                  >
                    <SaasAssetsTable
                      assets={Object.values(saasAssetsByProvider).flat()}
                      loading={assetsLoading || saasLoading}
                    />
                  </DataCard>
                </Col>
              </Row>
            </>
          ) : (
            <Alert
              message="No SaaS Protection Configured"
              description={
                assetsLoading || saasLoading
                  ? 'Loading SaaS protection data...'
                  : 'This customer does not have SaaS applications protected by Datto, or data is still loading.'
              }
              type="info"
              showIcon
              style={{ marginBottom: 24 }}
            />
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default DattoPage;
